import type { HmCareOrdersListPrefill } from '@/api/CareOrderDetailFocus'
import type {
  UserHomeItem,
  UserListItem,
  UserMetaData,
  UserOrderStatItem,
  UserUpcomingService,
} from '@/api/userslist'
import { DEFAULT_USER_ORDER_STAT_LABELS } from '@/api/userslist'

export interface UserOrderDisplayLine {
  label: string
  count?: number
  display: string
  statKey?: string
  orderId?: string
}

export type OrderStatLabelMap = Record<string, string>

function pickOrderId(raw: unknown): string {
  if (typeof raw === 'string' || typeof raw === 'number') {
    const id = String(raw).trim()
    return id || ''
  }
  return ''
}

function readOrderStatOrderId(record: Record<string, unknown>, key: string): string {
  const base = key.replace(/_orders$/, '')
  const candidates = [
    `${key}_order_id`,
    `${base}_order_id`,
    key === 'hmc_orders' || key === 'hmc' ? 'hmc_order_id' : '',
    key === 'fu_orders' || key === 'fu' ? 'fu_order_id' : '',
    key === 'ad_orders' || key === 'ad' ? 'ad_order_id' : '',
  ]
  for (const candidate of candidates) {
    if (!candidate) continue
    const id = pickOrderId(record[candidate])
    if (id) return id
  }
  return ''
}

function normalizeOrderLine(raw: unknown): UserOrderDisplayLine | null {
  if (raw == null) return null

  if (typeof raw === 'string') {
    const text = raw.trim()
    return text ? { label: '', display: text } : null
  }

  if (typeof raw !== 'object') return null

  const item = raw as UserOrderStatItem
  const display = typeof item.display === 'string' ? item.display.trim() : ''
  const text = typeof item.text === 'string' ? item.text.trim() : ''
  const label = typeof item.label === 'string' ? item.label.trim() : ''
  const count = typeof item.count === 'number' ? item.count : undefined
  const statKey =
    typeof item.key === 'string'
      ? item.key.trim()
      : typeof item.stat_key === 'string'
        ? item.stat_key.trim()
        : undefined
  const orderId = pickOrderId(item.order_id ?? item.orderId) || undefined

  if (display) return { label, count, display, statKey, orderId }
  if (text) return { label, count, display: text, statKey, orderId }
  if (label && count != null) return { label, count, display: '', statKey, orderId }
  if (label) return { label, display: label, statKey, orderId }

  return null
}

function linesFromOrderStatItems(items: unknown[] | undefined): UserOrderDisplayLine[] {
  if (!items?.length) return []
  return items
    .map(normalizeOrderLine)
    .filter((line): line is UserOrderDisplayLine => line != null)
}

const ORDER_STAT_KEYS = ['hmc_orders', 'fu_orders', 'ad_orders', 'hmc', 'fu', 'ad'] as const

function readOrderStatLabel(record: Record<string, unknown>, key: string): string {
  const labelKey = key.endsWith('_orders') ? `${key}_label` : `${key}_orders_label`
  const fromRecord = record[labelKey] ?? record[`${key}_label`]
  return typeof fromRecord === 'string' ? fromRecord.trim() : ''
}

function readOrderStatDisplay(record: Record<string, unknown>, key: string): string {
  const displayKey = key.endsWith('_orders') ? `${key}_display` : `${key}_orders_display`
  const fromRecord = record[displayKey] ?? record[`${key}_display`]
  return typeof fromRecord === 'string' ? fromRecord.trim() : ''
}

function resolveOrderStatLabel(
  key: string,
  record: Record<string, unknown>,
  labelMap: OrderStatLabelMap,
): string {
  const fromList = readOrderStatLabel(record, key)
  if (fromList) return fromList
  const fromMeta = labelMap[key]?.trim() || labelMap[`${key}_label`]?.trim()
  if (fromMeta) return fromMeta
  return DEFAULT_USER_ORDER_STAT_LABELS[key] ?? ''
}

function linesFromOrderStatsObject(
  record: Record<string, unknown>,
  labelMap: OrderStatLabelMap,
): UserOrderDisplayLine[] {
  if (Array.isArray(record.items)) {
    return linesFromOrderStatItems(record.items)
  }

  const lines: UserOrderDisplayLine[] = []

  for (const key of ORDER_STAT_KEYS) {
    const value = record[key]
    if (value == null) continue

    if (typeof value === 'object' && !Array.isArray(value)) {
      const nested = normalizeOrderLine(value)
      if (nested) {
        lines.push({
          ...nested,
          statKey: nested.statKey ?? key,
          orderId: nested.orderId || readOrderStatOrderId(record, key) || undefined,
        })
      }
      continue
    }

    if (typeof value !== 'number' || value <= 0) continue

    const orderId = readOrderStatOrderId(record, key) || undefined
    const display = readOrderStatDisplay(record, key)
    if (display) {
      lines.push({
        label: readOrderStatLabel(record, key) || resolveOrderStatLabel(key, record, labelMap),
        count: value,
        display,
        statKey: key,
        orderId,
      })
      continue
    }

    const label = resolveOrderStatLabel(key, record, labelMap)
    if (label) {
      lines.push({ label, count: value, display: '', statKey: key, orderId })
    }
  }

  return lines
}

export function buildUserOrderStatLabelMap(
  meta: UserMetaData | null | undefined,
): OrderStatLabelMap {
  const map: OrderStatLabelMap = {}

  const rawLabels = meta?.order_stat_labels
  if (rawLabels && typeof rawLabels === 'object') {
    for (const [key, value] of Object.entries(rawLabels)) {
      if (typeof value === 'string' && value.trim()) {
        map[key] = value.trim()
      }
    }
  }

  const typeSources = [meta?.order_stat_types, meta?.order_types]
  for (const source of typeSources) {
    if (!Array.isArray(source)) continue
    for (const entry of source) {
      if (typeof entry === 'string') {
        map[entry] = entry
        continue
      }
      const value = String(entry.value ?? '').trim()
      const label = String(entry.label ?? '').trim()
      if (value && label) map[value] = label
    }
  }

  return map
}

type UserOrderLineSource = Pick<UserListItem, 'order_stats' | 'order_stat_items' | 'no_orders_text'> &
  Partial<Pick<UserHomeItem, 'hmc_orders' | 'fu_orders' | 'ad_orders'>>

export function resolveUserOrderLines(
  item: UserOrderLineSource,
  labelMap: OrderStatLabelMap = {},
): UserOrderDisplayLine[] {
  let lines: UserOrderDisplayLine[] = []

  if (item.order_stat_items?.length) {
    lines = linesFromOrderStatItems(item.order_stat_items)
  } else {
    const orderStats = item.order_stats
    if (Array.isArray(orderStats)) {
      lines = linesFromOrderStatItems(orderStats)
    } else if (orderStats && typeof orderStats === 'object') {
      lines = linesFromOrderStatsObject(orderStats as Record<string, unknown>, labelMap)
    }
  }

  if (lines.length === 0) {
    lines = linesFromOrderStatsObject(item as Record<string, unknown>, labelMap)
  }

  if (lines.length > 0) return lines

  const noOrdersText = item.no_orders_text?.trim()
  return noOrdersText ? [{ label: '', display: noOrdersText }] : []
}

/** 下一次检查订单号形态（避免被 format 成 Title Case） */
export function looksLikeOrderId(raw: string): boolean {
  const s = raw.trim()
  if (!s || /\s/.test(s)) return false
  if (/^none$/i.test(s)) return false
  // 含数字的无空格串，视为订单号（如 SEED-ORD-TODAY-003 / 20260806093001）
  return /\d/.test(s)
}

/** 套餐展示名：premium_care / Premium_Care → Premium Care；订单号原样保留 */
export function formatPlanDisplayLabel(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (/^none$/i.test(trimmed)) return 'None'
  if (looksLikeOrderId(trimmed)) return trimmed
  return trimmed
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function pickUpcomingOrderId(
  upcomingService: UserUpcomingService | string | null | undefined,
  displayLabel?: string,
): string {
  if (upcomingService && typeof upcomingService === 'object') {
    const keys = ['order_id', 'orderId', 'care_order_id', 'careOrderId'] as const
    for (const key of keys) {
      const raw = upcomingService[key]
      const id = typeof raw === 'string' || typeof raw === 'number' ? String(raw).trim() : ''
      if (id) return id
    }
  }
  if (typeof upcomingService === 'string' && upcomingService.trim()) {
    const id = upcomingService.trim()
    if (looksLikeOrderId(id)) return id
  }
  const label = displayLabel?.trim() ?? ''
  if (label && looksLikeOrderId(label)) return label
  return ''
}

export function resolveUpcomingPlanLabel(
  item: Pick<UserListItem, 'upcoming_service' | 'upcoming_none_text' | 'upcoming_plan_label'>,
): string {
  const topLevel = item.upcoming_plan_label?.trim()
  if (topLevel) return formatPlanDisplayLabel(topLevel)

  const upcomingService = item.upcoming_service
  if (typeof upcomingService === 'string' && upcomingService.trim()) {
    return formatPlanDisplayLabel(upcomingService)
  }

  if (upcomingService && typeof upcomingService === 'object') {
    const svc = upcomingService
    const labelCandidates = [
      svc.plan_name,
      svc.plan_label,
      svc.label,
      svc.name,
      svc.title,
      svc.service_name,
      svc.plan_key,
    ]
    for (const candidate of labelCandidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return formatPlanDisplayLabel(candidate)
      }
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        return formatPlanDisplayLabel(String(candidate))
      }
    }
  }

  const fallback = item.upcoming_none_text?.trim() || (upcomingService == null ? 'None' : '')
  return fallback ? formatPlanDisplayLabel(fallback) : ''
}

function composeUpcomingDisplayFromParts(service: UserUpcomingService): string {
  const display = typeof service.display === 'string' ? service.display.trim() : ''
  if (display) return display

  const timeText = typeof service.time_text === 'string' ? service.time_text.trim() : ''
  if (timeText) return timeText

  const date =
    (typeof service.service_date === 'string' ? service.service_date.trim() : '') ||
    (typeof service.date === 'string' ? service.date.trim() : '')
  const timeStart = typeof service.time_start === 'string' ? service.time_start.trim() : ''
  const timeEnd = typeof service.time_end === 'string' ? service.time_end.trim() : ''

  if (date && timeStart && timeEnd) return `${date}, ${timeStart} - ${timeEnd}`
  if (date && timeStart) return `${date}, ${timeStart}`
  if (date) return date
  return ''
}

export function resolveUpcomingDisplay(
  item: Pick<UserListItem, 'upcoming_service' | 'upcoming_display'>,
): string {
  const topLevel = item.upcoming_display?.trim()
  if (topLevel) return topLevel

  const upcomingService = item.upcoming_service
  if (upcomingService && typeof upcomingService === 'object') {
    return composeUpcomingDisplayFromParts(upcomingService)
  }

  return ''
}

export function resolveUpcomingService(
  raw: UserListItem['upcoming_service'],
): UserUpcomingService | string | null {
  if (raw == null) return null
  if (typeof raw === 'string') return raw
  return raw
}

export function resolveUserDisplayId(item: UserListItem): string {
  return (
    item.user_no?.trim() ||
    item.user_id?.trim() ||
    (item.id != null ? String(item.id) : '')
  )
}

export function resolvePlanLabel(
  item: {
    plan_label?: string
    care_plan?: string
    care_plan_label?: string
    subscription_text?: string
    subscribed_plan?: string
    is_subscribed?: boolean | number
  },
): { text: string; subscribed: boolean } {
  const raw =
    item.subscribed_plan?.trim() ||
    item.plan_label?.trim() ||
    item.care_plan_label?.trim() ||
    item.subscription_text?.trim() ||
    item.care_plan?.trim() ||
    ''

  if (item.is_subscribed === 0 || item.is_subscribed === false) {
    return { text: raw || 'Not subscribed', subscribed: false }
  }

  if (!raw || /^not\s*subscribed$/i.test(raw)) {
    return { text: raw || 'Not subscribed', subscribed: false }
  }

  return { text: raw, subscribed: true }
}

/** 有 homes 数组则原样返回（含空数组）；未返回 homes 时把顶层单地址收成一行 */
export function resolveUserHomes(item: UserListItem): UserHomeItem[] {
  if (Array.isArray(item.homes)) {
    return item.homes
  }
  const top = item as UserListItem & Partial<UserHomeItem>
  return [
    {
      home_id: top.home_id,
      address: top.address,
      type: top.type,
      home_type: top.home_type,
      year_built: top.year_built,
      bedrooms: top.bedrooms,
      bathrooms: top.bathrooms,
      size: top.size,
      size_label: top.size_label,
      plan_label: top.plan_label,
      care_plan: top.care_plan,
      care_plan_label: top.care_plan_label,
      subscription_text: top.subscription_text,
      subscribed_plan: top.subscribed_plan,
      subscribed_plan_key: top.subscribed_plan_key,
      subscription_id: top.subscription_id,
      is_subscribed: top.is_subscribed,
      order_stats: top.order_stats,
      order_stat_items: top.order_stat_items,
      hmc_orders: top.hmc_orders,
      fu_orders: top.fu_orders,
      ad_orders: top.ad_orders,
      hmc_order_id: top.hmc_order_id,
      fu_order_id: top.fu_order_id,
      ad_order_id: top.ad_order_id,
      no_orders_text: top.no_orders_text,
      upcoming_service: top.upcoming_service,
      upcoming_none_text: top.upcoming_none_text,
      upcoming_plan_label: top.upcoming_plan_label,
      upcoming_display: top.upcoming_display,
    },
  ]
}

export function normalizeMobileSearchKeyword(keyword: string): string {
  return keyword.replace(/[^\d]/g, '')
}

/** Users / User Detail 跳转 HM Care Orders 列表时的搜索预填（home_id 优先，user_id 兜底） */
export function buildUserHomeOrdersListPrefill(
  homeId: string,
  userId: string,
): HmCareOrdersListPrefill | null {
  const hid = homeId.trim()
  if (hid && hid.toUpperCase() !== 'N/A') {
    return { searchField: 'home_id', searchQuery: hid, careSubKey: 'hm_orders' }
  }
  const uid = userId.trim()
  if (uid) return { searchField: 'user_id', searchQuery: uid, careSubKey: 'hm_orders' }
  return null
}
